import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import HomeExperienceCard from "./HomeExperienceCard";
import { AppButton } from "../ui";
import API from "../../services/API";
import ExperiencesAPI from "../../services/ExperiencesAPI";
import { FEATURED_COLLECTION_ID } from "../../services/featuredExperiences";
import { useContent } from "../../context/ContentContext";
import { useExperiences } from "../../context/ExperiencesContext";
import { useAuth } from "../../context/AuthContext";
import JoinCodeDialog from "../JoinCodeDialog";
import CreateExperienceDialog from "../experiences/CreateExperienceDialog";
import EditExperienceDialog from "../experiences/EditExperienceDialog";
import { isEducatorRole } from "../../utility/accountRoles";
import "./HomeExperienceRail.scss";

const COLLECTION_ITEM_TYPE_EXPERIENCE = 1;
const DEFAULT_DESCRIPTION = "Preview this experience in Viewer.";
const DEFAULT_ORGANIZATION_DESCRIPTION =
  "Launch this classroom experience in Viewer or generate a join code for students.";
const DEFAULT_AVERAGE_LENGTH = "15 min";
const SKELETON_CARD_COUNT = 8;
const ORGANIZATION_SKELETON_CARD_COUNT = 4;

const featuredCollectionCache = new Map();
const experienceDetailsCache = new Map();
const systemDetailsCache = new Map();
const guideDetailsCache = new Map();

const getCachedRequest = (cache, key, requestFn) => {
  if (!key) {
    return Promise.resolve(null);
  }

  if (cache.has(key)) {
    return cache.get(key);
  }

  const requestPromise = Promise.resolve()
    .then(requestFn)
    .catch((error) => {
      cache.delete(key);
      throw error;
    });

  cache.set(key, requestPromise);
  return requestPromise;
};

const getCollectionPayload = (responseData) => {
  return responseData?.data || responseData || {};
};

const getCardTitle = (item) =>
  item?.name || item?.title || item?.metadata?.name || "Untitled Experience";

const getCardDescription = (item) =>
  item?.description || item?.summary || item?.metadata?.description || DEFAULT_DESCRIPTION;

const getNumericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const getFirstPositiveNumericValue = (...values) => {
  for (const value of values) {
    const numericValue = getNumericValue(value);
    if (numericValue !== null && numericValue > 0) {
      return numericValue;
    }
  }

  return null;
};

const getFirstNumericValue = (...values) => {
  for (const value of values) {
    const numericValue = getNumericValue(value);
    if (numericValue !== null) {
      return numericValue;
    }
  }

  return null;
};

const getCollectionItemOrder = (item) => getFirstNumericValue(item?.order, item?.metadata?.order);

const getExperienceItems = (collectionPayload) => {
  const items = Array.isArray(collectionPayload?.items) ? collectionPayload.items : [];

  return items
    .filter((item) => Number(item?.type) === COLLECTION_ITEM_TYPE_EXPERIENCE && item?.id)
    .sort((a, b) => {
      const aOrder = getCollectionItemOrder(a);
      const bOrder = getCollectionItemOrder(b);

      if (aOrder === null && bOrder === null) {
        return getCardTitle(a).localeCompare(getCardTitle(b));
      }

      if (aOrder === null) {
        return 1;
      }

      if (bOrder === null) {
        return -1;
      }

      if (aOrder === bOrder) {
        return getCardTitle(a).localeCompare(getCardTitle(b));
      }

      return aOrder - bOrder;
    });
};

const getFirstTextValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const normalizeTopics = (rawTopics) => {
  const topicValues = Array.isArray(rawTopics)
    ? rawTopics
    : typeof rawTopics === "string" && rawTopics.trim()
    ? rawTopics.split(/[,\n|]/)
    : [];

  const seenTopics = new Set();

  return topicValues.reduce((normalizedTopics, topic) => {
    const trimmedTopic = typeof topic === "string" ? topic.trim() : "";
    if (!trimmedTopic) {
      return normalizedTopics;
    }

    const dedupeKey = trimmedTopic.toLowerCase();
    if (seenTopics.has(dedupeKey)) {
      return normalizedTopics;
    }

    seenTopics.add(dedupeKey);
    normalizedTopics.push(trimmedTopic);
    return normalizedTopics;
  }, []);
};

const getCardAverageLength = (item, experienceDetails) => {
  const collectionSizeMinutes = getFirstPositiveNumericValue(
    item?.size,
    item?.metadata?.size,
    experienceDetails?.size,
    experienceDetails?.metadata?.size
  );

  if (collectionSizeMinutes !== null) {
    return `${Math.round(collectionSizeMinutes)} min`;
  }

  const directValue = getFirstTextValue(
    item?.sizeLabel,
    item?.metadata?.sizeLabel,
    experienceDetails?.sizeLabel,
    experienceDetails?.metadata?.sizeLabel,
    experienceDetails?.averageLength,
    experienceDetails?.runtime,
    experienceDetails?.duration,
    experienceDetails?.metadata?.averageLength,
    experienceDetails?.metadata?.runtime,
    experienceDetails?.metadata?.duration,
    item?.averageLength,
    item?.runtime,
    item?.duration,
    item?.metadata?.averageLength,
    item?.metadata?.runtime,
    item?.metadata?.duration
  );

  if (directValue) {
    return directValue;
  }

  const numericMinutes = getFirstPositiveNumericValue(
    experienceDetails?.averageLengthMinutes,
    experienceDetails?.runtimeMinutes,
    experienceDetails?.durationMinutes,
    experienceDetails?.metadata?.averageLengthMinutes,
    experienceDetails?.metadata?.runtimeMinutes,
    experienceDetails?.metadata?.durationMinutes,
    item?.averageLengthMinutes,
    item?.runtimeMinutes,
    item?.durationMinutes,
    item?.metadata?.averageLengthMinutes,
    item?.metadata?.runtimeMinutes,
    item?.metadata?.durationMinutes
  );

  if (numericMinutes !== null) {
    return `${Math.round(numericMinutes)} min`;
  }

  return DEFAULT_AVERAGE_LENGTH;
};

const getCardTopics = (item, experienceDetails) => {
  const topicCandidates = [
    item?.topics,
    item?.metadata?.topics,
    experienceDetails?.topics,
    experienceDetails?.metadata?.topics,
  ];

  for (const topicCandidate of topicCandidates) {
    const parsedTopics = normalizeTopics(topicCandidate);
    if (parsedTopics.length > 0) {
      return parsedTopics;
    }
  }

  return [];
};

const getCardFullText = (item, experienceDetails, fallbackDescription) => {
  return getFirstTextValue(
    experienceDetails?.fullText,
    experienceDetails?.longDescription,
    experienceDetails?.text,
    experienceDetails?.metadata?.fullText,
    experienceDetails?.metadata?.longDescription,
    experienceDetails?.metadata?.text,
    item?.fullText,
    item?.longDescription,
    item?.text,
    item?.metadata?.fullText,
    item?.metadata?.longDescription,
    item?.metadata?.text,
    fallbackDescription
  );
};

const getExperiencePayload = (responseData) => responseData?.data || responseData || {};
const getGuidePayload = (responseData) => responseData?.data || responseData || {};
const getSystemPayload = (responseData) => responseData?.data || responseData || {};

const getGuideInsightCount = (guideDetails) => {
  if (Array.isArray(guideDetails?.insightDataIds)) {
    return guideDetails.insightDataIds.length;
  }

  if (Array.isArray(guideDetails?.insightDatas)) {
    return guideDetails.insightDatas.length;
  }

  return null;
};

const getSystemEnvironmentNames = (systemDetails, getEnvironmentName) => {
  if (!systemDetails || typeof systemDetails !== "object") {
    return [];
  }

  const environmentIds = [];
  const environmentNames = [];
  const environmentNamesById = new Map();

  const addEnvironmentId = (value) => {
    if (typeof value === "string" && value.trim()) {
      environmentIds.push(value.trim());
    }
  };

  const addEnvironmentName = (value) => {
    if (typeof value === "string" && value.trim()) {
      environmentNames.push(value.trim());
    }
  };

  const collectFromEnvironmentEntry = (entry, fallbackId) => {
    if (!entry) {
      addEnvironmentId(fallbackId);
      return;
    }

    if (typeof entry === "string") {
      addEnvironmentId(entry);
      return;
    }

    if (typeof entry !== "object") {
      addEnvironmentId(fallbackId);
      return;
    }

    const entryId = getFirstTextValue(entry.id, entry.environmentId, entry.environmentID, fallbackId);
    const entryName = getFirstTextValue(
      entry.environmentName,
      entry.name,
      entry.metadata?.name,
      entry.title
    );

    addEnvironmentId(entryId);
    addEnvironmentName(entryName);
    if (entryId && entryName) {
      environmentNamesById.set(entryId, entryName);
    }
  };

  if (Array.isArray(systemDetails.environmentIDs)) {
    systemDetails.environmentIDs.forEach((environmentId) => addEnvironmentId(environmentId));
  }

  if (Array.isArray(systemDetails.environments)) {
    systemDetails.environments.forEach((environmentEntry) => {
      collectFromEnvironmentEntry(environmentEntry);
    });
  } else if (systemDetails.environments && typeof systemDetails.environments === "object") {
    Object.entries(systemDetails.environments).forEach(([environmentId, environmentEntry]) => {
      collectFromEnvironmentEntry(environmentEntry, environmentId);
    });
  }

  if (Array.isArray(systemDetails.environmentDatas)) {
    systemDetails.environmentDatas.forEach((environmentEntry) => {
      collectFromEnvironmentEntry(environmentEntry);
    });
  } else if (
    systemDetails.environmentDatas &&
    typeof systemDetails.environmentDatas === "object"
  ) {
    Object.entries(systemDetails.environmentDatas).forEach(([environmentId, environmentEntry]) => {
      collectFromEnvironmentEntry(environmentEntry, environmentId);
    });
  }

  const uniqueNames = [];
  const seenNames = new Set();

  const pushUniqueName = (name) => {
    if (!name || seenNames.has(name)) {
      return;
    }
    seenNames.add(name);
    uniqueNames.push(name);
  };

  environmentNames.forEach((name) => {
    pushUniqueName(name);
  });

  environmentIds.forEach((environmentId) => {
    if (environmentNamesById.has(environmentId)) {
      return;
    }

    const resolvedName = getEnvironmentName(environmentId);
    pushUniqueName(resolvedName || environmentId);
  });

  return uniqueNames;
};

const fetchFeaturedCollection = async () => {
  const payload = await getCachedRequest(
    featuredCollectionCache,
    FEATURED_COLLECTION_ID,
    async () => {
      const response = await API.get(`collections/${FEATURED_COLLECTION_ID}`, {
        requiresAuth: true,
      });
      return getCollectionPayload(response?.data);
    }
  );

  return payload || {};
};

const fetchExperienceDetails = async (experienceId) => {
  if (!experienceId) {
    return null;
  }

  try {
    return await getCachedRequest(experienceDetailsCache, experienceId, async () => {
      const response = await ExperiencesAPI.get(`experiences/${experienceId}`, {
        requiresAuth: true,
      });
      return getExperiencePayload(response?.data);
    });
  } catch {
    return null;
  }
};

const fetchSystemDetails = async (systemId) => {
  if (!systemId) {
    return null;
  }

  try {
    return await getCachedRequest(systemDetailsCache, systemId, async () => {
      const response = await API.get(`systems/${systemId}`, {
        requiresAuth: true,
      });
      return getSystemPayload(response?.data);
    });
  } catch {
    return null;
  }
};

const fetchGuideDetails = async (guideId) => {
  if (!guideId) {
    return null;
  }

  try {
    return await getCachedRequest(guideDetailsCache, guideId, async () => {
      const response = await ExperiencesAPI.get(`guides/${guideId}`, {
        requiresAuth: true,
      });
      return getGuidePayload(response?.data);
    });
  } catch {
    return null;
  }
};

const fetchThumbnailUrl = async (thumbnailId, getThumbnail, options) => {
  if (!thumbnailId) {
    return "";
  }

  try {
    const thumbnailUrl = await getThumbnail(thumbnailId, options);
    return thumbnailUrl || "";
  } catch {
    return "";
  }
};

const getCardThumbnailUrl = async (thumbnailCandidates, getThumbnail, options) => {
  const uniqueCandidates = [...new Set(thumbnailCandidates.filter(Boolean))];

  for (const candidateId of uniqueCandidates) {
    const candidateThumbnail = await fetchThumbnailUrl(candidateId, getThumbnail, options);
    if (candidateThumbnail) {
      return candidateThumbnail;
    }
  }

  return "";
};

const getOrganizationExperienceId = (experience) =>
  getFirstTextValue(
    experience?.experienceId,
    experience?.id,
    experience?.metadata?.experienceId
  );

const getOrganizationExperienceSortTime = (experience) => {
  const firstValidDate = [
    experience?.metadata?.dateCreated,
    experience?.dateCreated,
    experience?.createdAt,
    experience?.metadata?.dateModified,
    experience?.lastModified,
    experience?.updatedAt,
  ].find((value) => Boolean(value));

  return new Date(firstValidDate || 0).getTime();
};

const getOrganizationCardDescription = (experience, systemDetails, guideDetails) => {
  const directDescription = getFirstTextValue(
    experience?.description,
    experience?.summary,
    experience?.metadata?.description,
    systemDetails?.description,
    systemDetails?.summary,
    systemDetails?.metadata?.description,
    guideDetails?.description,
    guideDetails?.summary,
    guideDetails?.metadata?.description
  );

  if (directDescription) {
    return directDescription;
  }

  const systemName = getFirstTextValue(systemDetails?.name, systemDetails?.metadata?.name);
  const guideName = getFirstTextValue(guideDetails?.name, guideDetails?.metadata?.name);

  if (systemName && guideName) {
    return `${systemName} with the ${guideName} guide.`;
  }

  if (systemName) {
    return `${systemName} classroom experience.`;
  }

  if (guideName) {
    return `Experience with the ${guideName} guide.`;
  }

  return DEFAULT_ORGANIZATION_DESCRIPTION;
};

const buildOrganizationExperienceCard = async (
  experience,
  { getThumbnail, getEnvironmentName }
) => {
  const experienceId = getOrganizationExperienceId(experience);
  if (!experienceId) {
    return null;
  }

  const systemId = getFirstTextValue(
    experience?.systemId,
    experience?.metadata?.systemId
  );
  const guideId = getFirstTextValue(
    experience?.guideId,
    experience?.metadata?.guideId
  );

  const [systemDetails, guideDetails, thumbnailUrl] = await Promise.all([
    fetchSystemDetails(systemId),
    fetchGuideDetails(guideId),
    getCardThumbnailUrl(
      [systemId, experience?.thumbnailId, experience?.imageId, experienceId],
      getThumbnail
    ),
  ]);

  const description = getOrganizationCardDescription(
    experience,
    systemDetails,
    guideDetails
  );

  return {
    id: experienceId,
    systemId,
    guideId,
    experienceRecord: experience,
    title: getCardTitle(experience),
    description,
    fullText: getCardFullText(experience, systemDetails, description),
    averageLength: getCardAverageLength(experience, systemDetails || experience),
    topics: getCardTopics(experience, systemDetails),
    systemName: getFirstTextValue(systemDetails?.name, systemDetails?.metadata?.name),
    guideName: getFirstTextValue(guideDetails?.name, guideDetails?.metadata?.name),
    guideInsightCount: getGuideInsightCount(guideDetails),
    environmentNames: getSystemEnvironmentNames(systemDetails, getEnvironmentName),
    detailsLoaded: true,
    thumbnailUrl,
    viewerUrl: `/viewer/${experienceId}`,
  };
};

const HomeExperienceRail = () => {
  const { getThumbnail, getEnvironmentName } = useContent();
  const { experiences } = useExperiences();
  const { permissions, roleName } = useAuth();
  const canViewContent = permissions?.viewWebContent === true;
  const canManageJoinCodes = permissions?.manageJoinCodes === true;
  const [featuredCards, setFeaturedCards] = useState([]);
  const [organizationCards, setOrganizationCards] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [joinCodeCard, setJoinCodeCard] = useState(null);
  const [editingExperience, setEditingExperience] = useState(null);
  const [isFeaturedLoading, setIsFeaturedLoading] = useState(true);
  const [isOrganizationLoading, setIsOrganizationLoading] = useState(false);
  const [isModalDetailsLoading, setIsModalDetailsLoading] = useState(false);
  const [featuredError, setFeaturedError] = useState("");
  const [organizationError, setOrganizationError] = useState("");
  const [modalImageErrored, setModalImageErrored] = useState(false);
  const [showAllEnvironments, setShowAllEnvironments] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const getEnvironmentNameRef = useRef(getEnvironmentName);
  const isEducatorAccount = isEducatorRole(roleName);

  useEffect(() => {
    getEnvironmentNameRef.current = getEnvironmentName;
  }, [getEnvironmentName]);

  useEffect(() => {
    setModalImageErrored(false);
    setShowAllEnvironments(false);
    setShowAllTopics(false);
  }, [activeCard?.id, activeCard?.thumbnailUrl]);

  useEffect(() => {
    let cancelled = false;

    const fetchFeaturedExperiences = async () => {
      setIsFeaturedLoading(true);
      setFeaturedError("");

      try {
        const payload = await fetchFeaturedCollection();
        const experienceItems = getExperienceItems(payload);
        const experienceDetailsById = new Map();
        const linkedIdsByExperienceId = new Map();

        await Promise.all(
          experienceItems.map(async (item) => {
            const experienceDetails = await fetchExperienceDetails(item.id);
            experienceDetailsById.set(item.id, experienceDetails);

            const systemId = getFirstTextValue(
              experienceDetails?.systemId,
              experienceDetails?.metadata?.systemId,
              item?.systemId
            );
            const guideId = getFirstTextValue(
              experienceDetails?.guideId,
              experienceDetails?.metadata?.guideId,
              item?.guideId
            );

            linkedIdsByExperienceId.set(item.id, { systemId, guideId });
          })
        );

        const cardsWithThumbnails = await Promise.all(
          experienceItems.map(async (item) => {
            const experienceDetails = experienceDetailsById.get(item.id) || null;
            const linkedIds = linkedIdsByExperienceId.get(item.id) || {};
            const systemId = linkedIds.systemId || "";
            const guideId = linkedIds.guideId || "";

            const thumbnailUrl = await getCardThumbnailUrl(
              [item?.id, item?.thumbnailId, item?.imageId, systemId],
              getThumbnail,
              { size: "large" }
            );

            const primaryDescription = getCardDescription(item);
            const fallbackDescription = getCardDescription(experienceDetails);
            const description =
              primaryDescription !== DEFAULT_DESCRIPTION
                ? primaryDescription
                : fallbackDescription;

            return {
              id: item.id,
              systemId,
              guideId,
              // Keep copy sourced from the curated collection item metadata.
              title: getCardTitle(item),
              description,
              fullText: getCardFullText(item, experienceDetails, description),
              averageLength: getCardAverageLength(item, experienceDetails),
              topics: getCardTopics(item, experienceDetails),
              systemName: "",
              guideName: "",
              guideInsightCount: null,
              environmentNames: [],
              detailsLoaded: false,
              thumbnailUrl,
              viewerUrl: `/viewer/${item.id}`,
            };
          })
        );

        if (!cancelled) {
          setFeaturedCards(cardsWithThumbnails);
        }
      } catch (fetchError) {
        console.error("Error loading featured experiences:", fetchError);
        if (!cancelled) {
          setFeaturedError("Unable to load featured experiences right now.");
        }
      } finally {
        if (!cancelled) {
          setIsFeaturedLoading(false);
        }
      }
    };

    fetchFeaturedExperiences();

    return () => {
      cancelled = true;
    };
  }, [getThumbnail]);

  useEffect(() => {
    let cancelled = false;

    if (!isEducatorAccount) {
      setOrganizationCards([]);
      setOrganizationError("");
      setIsOrganizationLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (experiences === null) {
      setOrganizationCards([]);
      setOrganizationError("");
      setIsOrganizationLoading(true);
      return () => {
        cancelled = true;
      };
    }

    const organizationExperiences = (experiences || [])
      .slice()
      .sort(
        (left, right) =>
          getOrganizationExperienceSortTime(right) - getOrganizationExperienceSortTime(left)
      );

    if (organizationExperiences.length === 0) {
      setOrganizationCards([]);
      setOrganizationError("");
      setIsOrganizationLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchOrganizationExperiences = async () => {
      setIsOrganizationLoading(true);
      setOrganizationError("");

      try {
        const nextCards = (
          await Promise.all(
            organizationExperiences.map((experience) =>
              buildOrganizationExperienceCard(experience, {
                getThumbnail,
                getEnvironmentName: getEnvironmentNameRef.current,
              })
            )
          )
        ).filter(Boolean);

        if (!cancelled) {
          setOrganizationCards(nextCards);
        }
      } catch (fetchError) {
        console.error("Error loading organization experiences:", fetchError);
        if (!cancelled) {
          setOrganizationError("Unable to load organization experiences right now.");
        }
      } finally {
        if (!cancelled) {
          setIsOrganizationLoading(false);
        }
      }
    };

    fetchOrganizationExperiences();

    return () => {
      cancelled = true;
    };
  }, [experiences, getThumbnail, isEducatorAccount]);

  const closeModal = () => {
    setActiveCard(null);
    setIsModalDetailsLoading(false);
  };

  const closeJoinCodeDialog = () => {
    setJoinCodeCard(null);
  };

  const closeEditExperienceDialog = () => {
    setEditingExperience(null);
  };

  const allCards = useMemo(
    () => [...featuredCards, ...organizationCards],
    [featuredCards, organizationCards]
  );

  const handleOpenCard = (card) => {
    const latestCard = allCards.find((candidate) => candidate.id === card.id) || card;
    setActiveCard(latestCard);
  };

  const handleOpenJoinCodeDialog = (card) => {
    const latestCard = allCards.find((candidate) => candidate.id === card.id) || card;
    setJoinCodeCard(latestCard);
  };

  const handleEditOrganizationExperience = (card) => {
    setEditingExperience(card?.experienceRecord || null);
  };

  useEffect(() => {
    if (!activeCard || activeCard.detailsLoaded) {
      return;
    }

    let cancelled = false;

    const loadModalDetails = async () => {
      setIsModalDetailsLoading(true);

      try {
        const [systemDetails, guideDetails] = await Promise.all([
          fetchSystemDetails(activeCard.systemId),
          fetchGuideDetails(activeCard.guideId),
        ]);

        if (cancelled) {
          return;
        }

        const enrichedCard = {
          ...activeCard,
          systemName: getFirstTextValue(systemDetails?.name, systemDetails?.metadata?.name),
          guideName: getFirstTextValue(guideDetails?.name, guideDetails?.metadata?.name),
          guideInsightCount: getGuideInsightCount(guideDetails),
          environmentNames: getSystemEnvironmentNames(
            systemDetails,
            getEnvironmentNameRef.current
          ),
          detailsLoaded: true,
        };

        setActiveCard(enrichedCard);
        setFeaturedCards((previousCards) =>
          previousCards.map((card) => (card.id === enrichedCard.id ? { ...card, ...enrichedCard } : card))
        );
        setOrganizationCards((previousCards) =>
          previousCards.map((card) => (card.id === enrichedCard.id ? { ...card, ...enrichedCard } : card))
        );
      } finally {
        if (!cancelled) {
          setIsModalDetailsLoading(false);
        }
      }
    };

    loadModalDetails();

    return () => {
      cancelled = true;
    };
  }, [activeCard]);

  const hasFeaturedCards = useMemo(() => featuredCards.length > 0, [featuredCards.length]);
  const hasOrganizationCards = useMemo(
    () => organizationCards.length > 0,
    [organizationCards.length]
  );
  const featuredSkeletonCardIndexes = useMemo(
    () => Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => index),
    []
  );
  const organizationSkeletonCardIndexes = useMemo(
    () => Array.from({ length: ORGANIZATION_SKELETON_CARD_COUNT }, (_, index) => index),
    []
  );

  return (
    <section className="homeExperienceRail">
      <div className="homeExperienceRail__section">
        <div className="homeExperienceRail__header">
          <h2 className="homeExperienceRail__title">10k Science Content Library</h2>
        </div>

        {isFeaturedLoading && (
          <div className="homeExperienceRail__grid homeExperienceRail__grid--loading" aria-live="polite">
            {featuredSkeletonCardIndexes.map((index) => (
              <article key={`home-skeleton-${index}`} className="homeExperienceCard homeExperienceCard--skeleton">
                <div className="homeExperienceCard__media homeExperienceCard__media--skeleton" />
                <div className="homeExperienceCard__body">
                  <div className="homeExperienceCard__line homeExperienceCard__line--title" />
                  <div className="homeExperienceCard__line homeExperienceCard__line--description" />
                  <div className="homeExperienceCard__line homeExperienceCard__line--descriptionShort" />
                  <div className="homeExperienceCard__line homeExperienceCard__line--meta" />
                  <div className="homeExperienceCard__line homeExperienceCard__line--button" />
                </div>
              </article>
            ))}
          </div>
        )}

        {!isFeaturedLoading && featuredError && (
          <div className="homeExperienceRail__status homeExperienceRail__status--error">
            {featuredError}
          </div>
        )}

        {!isFeaturedLoading && !featuredError && !hasFeaturedCards && (
          <div className="homeExperienceRail__status">
            No featured experiences available yet.
          </div>
        )}

        {!isFeaturedLoading && !featuredError && hasFeaturedCards && (
          <div className="homeExperienceRail__grid">
            {featuredCards.map((card) => (
              <HomeExperienceCard
                key={card.id}
                card={card}
                onOpen={handleOpenCard}
                onJoinCode={handleOpenJoinCodeDialog}
              />
            ))}
          </div>
        )}
      </div>

      {isEducatorAccount && (
        <div className="homeExperienceRail__section">
          <div className="homeExperienceRail__header homeExperienceRail__header--withAction">
            <div>
              <h2 className="homeExperienceRail__title">Organization Experiences</h2>
              <p className="homeExperienceRail__subtitle">
                Create classroom experiences and manage join codes for your organization.
              </p>
            </div>
            <AppButton
              kind="primary"
              className="homeExperienceRail__headerAction"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              Create Experience
            </AppButton>
          </div>

          {isOrganizationLoading && (
            <div className="homeExperienceRail__grid homeExperienceRail__grid--loading" aria-live="polite">
              {organizationSkeletonCardIndexes.map((index) => (
                <article key={`organization-home-skeleton-${index}`} className="homeExperienceCard homeExperienceCard--skeleton">
                  <div className="homeExperienceCard__media homeExperienceCard__media--skeleton" />
                  <div className="homeExperienceCard__body">
                    <div className="homeExperienceCard__line homeExperienceCard__line--title" />
                    <div className="homeExperienceCard__line homeExperienceCard__line--description" />
                    <div className="homeExperienceCard__line homeExperienceCard__line--descriptionShort" />
                    <div className="homeExperienceCard__line homeExperienceCard__line--meta" />
                    <div className="homeExperienceCard__line homeExperienceCard__line--button" />
                  </div>
                </article>
              ))}
            </div>
          )}

          {!isOrganizationLoading && organizationError && (
            <div className="homeExperienceRail__status homeExperienceRail__status--error">
              {organizationError}
            </div>
          )}

          {!isOrganizationLoading && !organizationError && !hasOrganizationCards && (
            <div className="homeExperienceRail__status">
              No organization experiences available yet.
            </div>
          )}

          {!isOrganizationLoading && !organizationError && hasOrganizationCards && (
            <div className="homeExperienceRail__grid">
              {organizationCards.map((card) => (
                <HomeExperienceCard
                  key={card.id}
                  card={card}
                  onOpen={handleOpenCard}
                  onJoinCode={handleOpenJoinCodeDialog}
                  onEdit={handleEditOrganizationExperience}
                  showEditButton
                />
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={Boolean(activeCard)}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          className: "homeExperienceModal__backdrop",
        }}
        PaperProps={{
          className: "homeExperienceModal__paper",
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            boxSizing: "border-box",
            padding: { xs: "12px", sm: "16px" },
            paddingTop: {
              xs: "calc(var(--layout-header-height, 80px) + 12px)",
              sm: "calc(var(--layout-header-height, 80px) + 16px)",
            },
          },
        }}
      >
        {activeCard && (
          <div className="homeExperienceModal">
            <button
              type="button"
              className="homeExperienceModal__close"
              onClick={closeModal}
              aria-label="Close experience details"
            >
              ×
            </button>

            <div className="homeExperienceModal__hero">
              {activeCard.thumbnailUrl && !modalImageErrored ? (
                <img
                  className="homeExperienceModal__heroImage"
                  src={activeCard.thumbnailUrl}
                  alt={`${activeCard.title} preview`}
                  onError={() => setModalImageErrored(true)}
                />
              ) : (
                <div className="homeExperienceModal__heroFallback">
                  <span>{activeCard.title}</span>
                </div>
              )}

              <div className="homeExperienceModal__heroOverlay">
                <h3 className="homeExperienceModal__title">{activeCard.title}</h3>
                <div className="homeExperienceModal__meta">
                  <span className="homeExperienceModal__metaItem">
                    Typical Session Length: {activeCard.averageLength || DEFAULT_AVERAGE_LENGTH}
                  </span>
                </div>
                <p className="homeExperienceModal__description">{activeCard.description}</p>
              </div>
            </div>

            <div className="homeExperienceModal__content">
              <h4 className="homeExperienceModal__environmentsHeading">Environments</h4>
              {isModalDetailsLoading ? (
                <p className="homeExperienceModal__topicsEmpty">Loading experience details...</p>
              ) : activeCard.environmentNames?.length > 0 ? (
                <ul className="homeExperienceModal__environmentsList">
                  {(showAllEnvironments ? activeCard.environmentNames : activeCard.environmentNames.slice(0, 6)).map((environmentName, index) => (
                    <li key={`${environmentName}-${index}`} className="homeExperienceModal__environmentChip">
                      {environmentName}
                    </li>
                  ))}
                  {!showAllEnvironments && activeCard.environmentNames.length > 6 && (
                    <li>
                      <button className="homeExperienceModal__chipMore" onClick={() => setShowAllEnvironments(true)}>
                        +{activeCard.environmentNames.length - 6} more
                      </button>
                    </li>
                  )}
                </ul>
              ) : (
                <p className="homeExperienceModal__topicsEmpty">Environment list coming soon.</p>
              )}

              <h4 className="homeExperienceModal__topicsHeading">Topics</h4>
              {activeCard.topics?.length > 0 ? (
                <ul className="homeExperienceModal__topicsList">
                  {(showAllTopics ? activeCard.topics : activeCard.topics.slice(0, 8)).map((topic, index) => (
                    <li key={`${topic}-${index}`} className="homeExperienceModal__topicChip">
                      {topic}
                    </li>
                  ))}
                  {!showAllTopics && activeCard.topics.length > 8 && (
                    <li>
                      <button className="homeExperienceModal__chipMore" onClick={() => setShowAllTopics(true)}>
                        +{activeCard.topics.length - 8} more
                      </button>
                    </li>
                  )}
                </ul>
              ) : (
                <p className="homeExperienceModal__topicsEmpty">Topics coming soon.</p>
              )}

              <section className="homeExperienceModal__detailsCard">
                <h4 className="homeExperienceModal__detailsHeading">Experience details</h4>
                <dl className="homeExperienceModal__detailsList">
                  <div className="homeExperienceModal__detailsRow">
                    <dt>System</dt>
                    <dd>{isModalDetailsLoading ? "Loading..." : activeCard.systemName || "Not listed"}</dd>
                  </div>
                  <div className="homeExperienceModal__detailsRow">
                    <dt>Guide</dt>
                    <dd>{isModalDetailsLoading ? "Loading..." : activeCard.guideName || "Not listed"}</dd>
                  </div>
                  <div className="homeExperienceModal__detailsRow">
                    <dt>Guide Steps</dt>
                    <dd>
                      {isModalDetailsLoading
                        ? "Loading..."
                        : Number.isFinite(activeCard.guideInsightCount)
                        ? activeCard.guideInsightCount
                        : "Not listed"}
                    </dd>
                  </div>
                </dl>
              </section>
            </div>

            <div className="homeExperienceModal__actions">
              <AppButton
                component={Link}
                to={canViewContent ? activeCard.viewerUrl : "/upgrade"}
                kind="primary"
                startIcon={<VisibilityOutlinedIcon />}
                className="homeExperienceModal__previewButton"
                onClick={closeModal}
              >
                {canViewContent ? "Preview" : "Upgrade to Preview"}
              </AppButton>
              {canManageJoinCodes && (
                <AppButton
                  kind="secondary"
                  className="homeExperienceModal__joinButton"
                  onClick={() => handleOpenJoinCodeDialog(activeCard)}
                >
                  Join Code
                </AppButton>
              )}
            </div>
          </div>
        )}
      </Dialog>
      <JoinCodeDialog
        open={Boolean(joinCodeCard)}
        onClose={closeJoinCodeDialog}
        experienceId={joinCodeCard?.id || ""}
        experienceName={joinCodeCard?.title || ""}
      />
      <CreateExperienceDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
      <EditExperienceDialog
        open={Boolean(editingExperience)}
        experience={editingExperience}
        onClose={closeEditExperienceDialog}
      />
    </section>
  );
};

export default HomeExperienceRail;
